import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Spin, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import {
    bulkUpdateOutreachProspects,
    cancelOutreachDiscoveryRun,
    cleanupSuppressedOutreachRows,
    getOutreachApprovedProspects,
    getOutreachAutomationRuns,
    getOutreachDeliveryControl,
    getOutreachDiagnostics,
    getOutreachDiscoveryRuns,
    getOutreachEmailEngagement,
    getOutreachEmailEvents,
    getOutreachPerformance,
    getOutreachProspectTimeline,
    getOutreachProspects,
    getOutreachSuppressions,
    getOutreachWarmLeads,
    markWarmLeadDoNotSend,
    processOutreachDiscoveryBatch,
    retryFailedOutreachFollowUps,
    runScheduledOutreachDiscoveryStep,
    runScheduledWarmLeadFollowUps,
    sendManualOutreachFollowUp,
    getSolmatixEmailSummary,
    getOutreachPreviewEmails,
    sendOutreachPreviewEmails,
    recordOutreachOutcome,
    triggerOutreachDiscoveryRun,
    triggerWarmLeadFollowUps,
    updateOutreachDeliveryControl,
    updateOutreachProspectDoNotSend,
    updateOutreachProspectMarketingBasis,
} from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const statusColor = {
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'default',
};

const defaultAppendTargets = {
    uk: 'outreach/south-england-installers-100-template.csv',
    us: 'outreach/us-installers.csv',
    de: 'outreach/de-installers.csv',
};

const ukRegionOptions = [
    'Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire', 'Cheshire', 'Cornwall',
    'County Durham', 'Cumbria', 'Derbyshire', 'Devon', 'Dorset', 'East Riding of Yorkshire', 'East Sussex',
    'Essex', 'Gloucestershire', 'Greater London', 'Greater Manchester', 'Hampshire', 'Herefordshire',
    'Hertfordshire', 'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire', 'Merseyside',
    'Norfolk', 'North Yorkshire', 'Northamptonshire', 'Northumberland', 'Nottinghamshire', 'Oxfordshire',
    'Rutland', 'Shropshire', 'Somerset', 'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey',
    'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex', 'West Yorkshire', 'Wiltshire',
    'Worcestershire', 'Scotland', 'Wales', 'Northern Ireland',
];

const usRegionOptions = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
    'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const deRegionOptions = [
    'Baden-Wuerttemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse',
    'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate',
    'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia',
];

const regionOptionsByCountry = {
    uk: ukRegionOptions,
    us: usRegionOptions,
    de: deRegionOptions,
};

const regionPlaceholderByCountry = {
    uk: 'Select a county/area',
    us: 'Select a state',
    de: 'Select a Bundesland',
};

const defaultEmailSummary = {
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    unsubscribed: 0,
    uniqueCampaigns: 0,
    totalEvents: 0,
    topLandingPages: [],
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-GB');
};

const parseDelimitedValues = (value) => {
    return String(value || '')
        .split(/[\n,]/)
        .map((entry) => entry.trim())
        .filter(Boolean);
};

const OutreachMonitor = () => {
    const pageLayoutProps = usePageLayout();
    const { role, user } = pageLayoutProps;
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [runs, setRuns] = useState([]);
    const [events, setEvents] = useState([]);
    const [engagement, setEngagement] = useState([]);
    const [warmLeads, setWarmLeads] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [solmatixSummary, setSolmatixSummary] = useState(defaultEmailSummary);
    const [eventType, setEventType] = useState(undefined);
    const [recipientFilter, setRecipientFilter] = useState('');
    const [appliedEventFilters, setAppliedEventFilters] = useState({ eventType: undefined, recipient: '' });
    const [prospectFilters, setProspectFilters] = useState({ country: 'uk', search: '' });
    const [prospectSearchDraft, setProspectSearchDraft] = useState('');
    const [followUpMinimumScore, setFollowUpMinimumScore] = useState(40);
    const [followUpLimit, setFollowUpLimit] = useState(20);
    const [followUpWindowDays, setFollowUpWindowDays] = useState(7);
    const [launchingDiscovery, setLaunchingDiscovery] = useState(false);
    const [launchCountry, setLaunchCountry] = useState('uk');
    const [launchRegion, setLaunchRegion] = useState('');
    const [launchCustomRegion, setLaunchCustomRegion] = useState('');
    const [launchLabel, setLaunchLabel] = useState('');
    const [launchSeedUrlsInput, setLaunchSeedUrlsInput] = useState('');
    const [launchListingPatternsInput, setLaunchListingPatternsInput] = useState('');
    const [launchAppendTo, setLaunchAppendTo] = useState('');
    const [lastLaunchResult, setLastLaunchResult] = useState(null);
    const [cleanupLoading, setCleanupLoading] = useState(false);
    const [cleanupCsvPathsInput, setCleanupCsvPathsInput] = useState('');
    const [cleanupResult, setCleanupResult] = useState(null);
    const [cancellingRunId, setCancellingRunId] = useState('');
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
    const [diagnosticsResult, setDiagnosticsResult] = useState(null);
    const [schedulerActionLoading, setSchedulerActionLoading] = useState('');
    const [schedulerActionResult, setSchedulerActionResult] = useState(null);
    const [approvedProspectsPreview, setApprovedProspectsPreview] = useState([]);
    const [suppressionHashesPreview, setSuppressionHashesPreview] = useState([]);
    const [followUpSending, setFollowUpSending] = useState(false);
    const [followUpResult, setFollowUpResult] = useState(null);
    const [previewRecipient, setPreviewRecipient] = useState('');
    const [previewSending, setPreviewSending] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewVariants, setPreviewVariants] = useState([]);
    const [activeOutreachTab, setActiveOutreachTab] = useState('overview');
    const [updatingProspectId, setUpdatingProspectId] = useState('');
    const [performance, setPerformance] = useState(null);
    const [recordingOutcomeRecipient, setRecordingOutcomeRecipient] = useState('');
    const [selectedProspectRowKeys, setSelectedProspectRowKeys] = useState([]);
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [manualFollowUpProspectId, setManualFollowUpProspectId] = useState('');
    const [prospectTimelines, setProspectTimelines] = useState({});
    const [timelineLoadingProspectId, setTimelineLoadingProspectId] = useState('');
    const [automationRuns, setAutomationRuns] = useState([]);
    const [operationsLoading, setOperationsLoading] = useState(false);
    const [deliveryControl, setDeliveryControl] = useState(null);
    const [retryingFailedFollowUps, setRetryingFailedFollowUps] = useState(false);
    const [suppressionDetailRows, setSuppressionDetailRows] = useState([]);
    const [markingWarmLeadRecipient, setMarkingWarmLeadRecipient] = useState('');

    useEffect(() => {
        if (user?.email) {
            setPreviewRecipient(user.email);
        }
    }, [user?.email]);

    const regionSelectOptions = useMemo(() => {
        return (regionOptionsByCountry[launchCountry] || []).map((value) => ({ value, label: value }));
    }, [launchCountry]);

    const loadData = useCallback(async () => {
        setLoading(true);
        const results = await Promise.allSettled([
            getOutreachDiscoveryRuns({ limit: 100 }),
            getOutreachEmailEvents({
                limit: 200,
                eventType: appliedEventFilters.eventType,
                recipient: appliedEventFilters.recipient || undefined,
            }),
            getOutreachEmailEngagement({ limit: 200 }),
            getOutreachWarmLeads({ limit: 50, minimumScore: 40 }),
            getOutreachProspects({
                limit: 300,
                country: prospectFilters.country || undefined,
                search: prospectFilters.search || undefined,
            }),
            getSolmatixEmailSummary({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
                endDate: new Date().toISOString(),
            }),
            getOutreachPerformance({ days: 30 }),
        ]);

        const [runsResult, eventsResult, engagementResult, warmLeadsResult, prospectsResult, solmatixResult, performanceResult] = results;
        if (runsResult.status === 'fulfilled') setRuns(Array.isArray(runsResult.value?.runs) ? runsResult.value.runs : []);
        if (eventsResult.status === 'fulfilled') setEvents(Array.isArray(eventsResult.value?.events) ? eventsResult.value.events : []);
        if (engagementResult.status === 'fulfilled') setEngagement(Array.isArray(engagementResult.value?.recipients) ? engagementResult.value.recipients : []);
        if (warmLeadsResult.status === 'fulfilled') setWarmLeads(Array.isArray(warmLeadsResult.value?.recipients) ? warmLeadsResult.value.recipients : []);
        if (prospectsResult.status === 'fulfilled') setProspects(Array.isArray(prospectsResult.value?.prospects) ? prospectsResult.value.prospects : []);
        if (solmatixResult.status === 'fulfilled') {
            setSolmatixSummary(solmatixResult.value || defaultEmailSummary);
        }
        if (performanceResult.status === 'fulfilled') setPerformance(performanceResult.value || null);

        const failures = results.filter((result) => result.status === 'rejected');
        if (failures.length > 0) {
            enqueueSnackbar(`Failed to load ${failures.length} of ${results.length} outreach datasets`, { variant: 'error' });
            failures.forEach((failure) => Sentry.captureException(failure.reason));
        }
        setLoading(false);
    }, [appliedEventFilters, enqueueSnackbar, prospectFilters]);

    const hasRunningRuns = useMemo(() => runs.some((run) => run.status === 'running'), [runs]);

    useEffect(() => {
        if (!hasRunningRuns) return undefined;
        const intervalId = setInterval(async () => {
            try {
                const response = await getOutreachDiscoveryRuns({ limit: 100 });
                setRuns(Array.isArray(response?.runs) ? response.runs : []);
            } catch (err) {
                // Silent refresh; manual refresh surfaces errors.
            }
        }, 15000);
        return () => clearInterval(intervalId);
    }, [hasRunningRuns]);

    const handleWarmLeadFollowUps = useCallback(async (dryRun) => {
        setFollowUpSending(true);
        try {
            const result = await triggerWarmLeadFollowUps({
                minimumScore: followUpMinimumScore,
                limit: followUpLimit,
                dryRun,
                followUpWindowDays,
            });
            setFollowUpResult(result || null);
            if (dryRun) {
                enqueueSnackbar(`Preview: ${result?.toSend?.length ?? 0} follow-up(s) would be sent`, { variant: 'info' });
            } else {
                enqueueSnackbar(`Warm-lead follow-ups sent: ${result?.sent ?? 0}`, { variant: 'success' });
                await loadData();
            }
        } catch (err) {
            const validationFields = err?.response?.data?.error?.fields;
            const validationMessage = validationFields ? Object.values(validationFields).flat()[0] : null;
            const apiMessage = validationMessage || err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to send warm-lead follow-ups', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setFollowUpSending(false);
        }
    }, [enqueueSnackbar, followUpLimit, followUpMinimumScore, followUpWindowDays, loadData]);

    const handlePreviewEmails = useCallback(async () => {
        const recipient = previewRecipient.trim();
        if (!recipient) {
            enqueueSnackbar('Enter a recipient email before sending outreach previews.', { variant: 'warning' });
            return;
        }

        setPreviewSending(true);
        try {
            const result = await sendOutreachPreviewEmails({ recipient });
            setPreviewResult(result || null);
            enqueueSnackbar(`Preview emails sent: ${result?.sent ?? 0}/${result?.total ?? 0}`, { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to send outreach preview emails', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setPreviewSending(false);
        }
    }, [enqueueSnackbar, previewRecipient]);

    const handleViewEmailPreviews = useCallback(async () => {
        const recipient = previewRecipient.trim();
        if (!recipient) {
            enqueueSnackbar('Enter a recipient email before viewing outreach previews.', { variant: 'warning' });
            return;
        }

        setPreviewLoading(true);
        try {
            const result = await getOutreachPreviewEmails({ recipient });
            setPreviewVariants(Array.isArray(result?.variants) ? result.variants : []);
            setPreviewModalOpen(true);
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to load outreach email previews', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setPreviewLoading(false);
        }
    }, [enqueueSnackbar, previewRecipient]);

    const handleProspectDoNotSendChange = useCallback(async (prospect, doNotSend) => {
        if (!prospect?._id) return;

        setUpdatingProspectId(prospect._id);
        try {
            const response = await updateOutreachProspectDoNotSend({
                prospectId: prospect._id,
                doNotSend,
            });
            const updatedProspect = response?.prospect;
            setProspects((current) => current.map((item) => (
                item._id === prospect._id ? { ...item, ...updatedProspect, doNotSend } : item
            )));
            enqueueSnackbar(doNotSend ? 'Prospect marked as do not send' : 'Prospect restored to outreach', { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to update prospect send preference', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const handleWarmLeadDoNotSend = useCallback(async (lead) => {
        const recipient = lead?.recipient?.trim().toLowerCase();
        if (!recipient) return;

        setMarkingWarmLeadRecipient(recipient);
        try {
            const response = await markWarmLeadDoNotSend({ recipient });
            const updatedProspect = response?.prospect || null;
            if (updatedProspect?._id) {
                setProspects((current) => {
                    const existingIndex = current.findIndex((item) => item._id === updatedProspect._id || item.email === updatedProspect.email);
                    if (existingIndex >= 0) {
                        return current.map((item) => (
                            item._id === updatedProspect._id || item.email === updatedProspect.email
                                ? { ...item, ...updatedProspect }
                                : item
                        ));
                    }
                    return [updatedProspect, ...current];
                });
            }
            await loadData();
            enqueueSnackbar('Warm lead marked as do not send', { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to mark warm lead as do not send', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setMarkingWarmLeadRecipient('');
        }
    }, [enqueueSnackbar, loadData]);

    const handleProspectMarketingBasisChange = useCallback(async (prospect, marketingBasis) => {
        if (!prospect?._id || !marketingBasis) return;

        setUpdatingProspectId(prospect._id);
        try {
            const response = await updateOutreachProspectMarketingBasis({
                prospectId: prospect._id,
                marketingBasis,
            });
            const updatedProspect = response?.prospect;
            setProspects((current) => current.map((item) => (
                item._id === prospect._id ? { ...item, ...updatedProspect, marketingBasis } : item
            )));
            enqueueSnackbar('Marketing basis updated', { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to update marketing basis', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const handleMarkProspectLegitimate = useCallback(async (prospect) => {
        if (!prospect?._id) return;
        setUpdatingProspectId(prospect._id);
        try {
            const [marketingBasisResponse, doNotSendResponse] = await Promise.all([
                updateOutreachProspectMarketingBasis({
                    prospectId: prospect._id,
                    marketingBasis: 'documented_legitimate_interest',
                }),
                updateOutreachProspectDoNotSend({
                    prospectId: prospect._id,
                    doNotSend: false,
                }),
            ]);
            const updatedProspect = {
                ...(marketingBasisResponse?.prospect || {}),
                ...(doNotSendResponse?.prospect || {}),
                marketingBasis: 'documented_legitimate_interest',
                doNotSend: false,
            };
            setProspects((current) => current.map((item) => (
                item._id === prospect._id ? { ...item, ...updatedProspect } : item
            )));
            enqueueSnackbar('Prospect marked as legitimate customer prospect', { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to mark legitimate customer prospect', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setUpdatingProspectId('');
        }
    }, [enqueueSnackbar]);

    const handleRecordOutcome = useCallback(async (recipient, outcome) => {
        if (!recipient || !outcome) return;

        setRecordingOutcomeRecipient(recipient);
        try {
            await recordOutreachOutcome({
                recipient,
                outcome,
                campaignId: 'warm-lead-queue',
                templateId: 'warm_lead_follow_up_v1',
                sourceKey: 'operator',
            });
            enqueueSnackbar('Outreach outcome recorded', { variant: 'success' });
            const refreshed = await getOutreachPerformance({ days: 30 });
            setPerformance(refreshed || null);
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to record outreach outcome', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setRecordingOutcomeRecipient('');
        }
    }, [enqueueSnackbar]);

    const handleLaunchDiscovery = useCallback(async () => {
        const region = (launchCustomRegion || launchRegion).trim();

        if (!region) {
            enqueueSnackbar('Please enter a county, US state, or German province', { variant: 'warning' });
            return;
        }

        setLaunchingDiscovery(true);
        try {
            const seedUrls = parseDelimitedValues(launchSeedUrlsInput);
            const listingLinkPatterns = parseDelimitedValues(launchListingPatternsInput);

            const launchResponse = await triggerOutreachDiscoveryRun({
                country: launchCountry,
                region,
                runLabel: launchLabel.trim() || undefined,
                seedUrl: seedUrls[0] || undefined,
                seedUrls: seedUrls.length > 0 ? seedUrls : undefined,
                listingLinkPattern: listingLinkPatterns[0] || undefined,
                listingLinkPatterns: listingLinkPatterns.length > 0 ? listingLinkPatterns : undefined,
                appendTo: launchAppendTo.trim() || undefined,
            });

            enqueueSnackbar('Discovery run started. Refreshing monitor...', { variant: 'success' });
            setLaunchRegion('');
            setLaunchCustomRegion('');
            setLaunchLabel('');
            setLaunchSeedUrlsInput('');
            setLaunchListingPatternsInput('');
            setLastLaunchResult(launchResponse || null);
            await loadData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to start discovery run', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLaunchingDiscovery(false);
        }
    }, [
        enqueueSnackbar,
        launchAppendTo,
        launchCountry,
        launchCustomRegion,
        launchLabel,
        launchListingPatternsInput,
        launchRegion,
        launchSeedUrlsInput,
        loadData,
    ]);

    const handleSuppressionCleanup = useCallback(async (dryRun) => {
        setCleanupLoading(true);
        try {
            const csvPaths = parseDelimitedValues(cleanupCsvPathsInput);

            const response = await cleanupSuppressedOutreachRows({
                dryRun,
                csvPaths: csvPaths.length > 0 ? csvPaths : undefined,
            });

            setCleanupResult(response || null);

            if (dryRun) {
                enqueueSnackbar('Suppression preview complete', { variant: 'info' });
            } else {
                enqueueSnackbar('Suppressed contacts updated', { variant: 'success' });
            }

            await loadData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to run suppression cleanup', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setCleanupLoading(false);
        }
    }, [cleanupCsvPathsInput, enqueueSnackbar, loadData]);

    const handleCancelRun = useCallback(async (runId) => {
        if (!runId) {
            return;
        }

        setCancellingRunId(runId);
        try {
            const response = await cancelOutreachDiscoveryRun({ runId });
            const cancelled = Boolean(response?.cancelled);
            enqueueSnackbar(cancelled ? 'Discovery run cancelled' : (response?.message || 'Run is no longer running'), {
                variant: cancelled ? 'success' : 'info',
            });
            await loadData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to cancel discovery run', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setCancellingRunId('');
        }
    }, [enqueueSnackbar, loadData]);

    const handleDiagnosticsCheck = useCallback(async () => {
        setDiagnosticsLoading(true);
        try {
            const response = await getOutreachDiagnostics();
            setDiagnosticsResult(response || null);
            enqueueSnackbar('Outreach diagnostics loaded', { variant: 'success' });
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to load outreach diagnostics', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setDiagnosticsLoading(false);
        }
    }, [enqueueSnackbar]);

    const handleSchedulerAction = useCallback(async (action) => {
        if (!action) return;
        setSchedulerActionLoading(action);
        try {
            let result;
            if (action === 'run-next') {
                result = await runScheduledOutreachDiscoveryStep();
            } else if (action === 'process-batch') {
                result = await processOutreachDiscoveryBatch();
            } else if (action === 'run-follow-ups') {
                result = await runScheduledWarmLeadFollowUps();
            } else if (action === 'view-approved-prospects') {
                result = await getOutreachApprovedProspects();
                setApprovedProspectsPreview(Array.isArray(result?.prospects) ? result.prospects : []);
            } else if (action === 'view-suppressions') {
                result = await getOutreachSuppressions();
                setSuppressionHashesPreview(Array.isArray(result?.hashes) ? result.hashes : []);
                setSuppressionDetailRows(Array.isArray(result?.suppressions) ? result.suppressions : []);
            }
            setSchedulerActionResult({ action, result: result || null });
            enqueueSnackbar('Scheduler endpoint action completed', { variant: 'success' });
            if (action !== 'view-approved-prospects' && action !== 'view-suppressions') {
                await loadData();
            }
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to run scheduler endpoint action', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setSchedulerActionLoading('');
        }
    }, [enqueueSnackbar, loadData]);

    const refreshOperationsData = useCallback(async () => {
        setOperationsLoading(true);
        try {
            const [runsResult, controlResult] = await Promise.all([
                getOutreachAutomationRuns({ limit: 100 }),
                getOutreachDeliveryControl(),
            ]);
            setAutomationRuns(Array.isArray(runsResult?.runs) ? runsResult.runs : []);
            setDeliveryControl(controlResult?.control || null);
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to load outreach operations data', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setOperationsLoading(false);
        }
    }, [enqueueSnackbar]);

    const handleDeliveryControlUpdate = useCallback(async (mode) => {
        const normalizedMode = mode === 'paused' ? 'paused' : 'auto';
        setOperationsLoading(true);
        try {
            const result = await updateOutreachDeliveryControl({
                mode: normalizedMode,
                reason: normalizedMode === 'paused'
                    ? 'Manually paused from super admin'
                    : 'Resumed from super admin',
            });
            setDeliveryControl(result?.control || null);
            enqueueSnackbar(normalizedMode === 'paused' ? 'Automated follow-ups paused' : 'Automated follow-ups resumed', { variant: 'success' });
            await loadData();
            await refreshOperationsData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to update delivery control', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setOperationsLoading(false);
        }
    }, [enqueueSnackbar, loadData, refreshOperationsData]);

    const handleRetryFailedFollowUps = useCallback(async () => {
        setRetryingFailedFollowUps(true);
        try {
            const result = await retryFailedOutreachFollowUps({ lookbackDays: 7, limit: 20 });
            enqueueSnackbar(`Retry run complete: ${result?.sent ?? 0} sent, ${result?.failed ?? 0} failed`, { variant: 'info' });
            await loadData();
            await refreshOperationsData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to retry failed follow-ups', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setRetryingFailedFollowUps(false);
        }
    }, [enqueueSnackbar, loadData, refreshOperationsData]);

    const handleBulkProspectUpdate = useCallback(async ({ doNotSend, marketingBasis }) => {
        if (!Array.isArray(selectedProspectRowKeys) || selectedProspectRowKeys.length === 0) {
            enqueueSnackbar('Select at least one prospect first', { variant: 'warning' });
            return;
        }

        setBulkUpdating(true);
        try {
            const result = await bulkUpdateOutreachProspects({
                prospectIds: selectedProspectRowKeys,
                doNotSend,
                marketingBasis,
            });
            const updatedRows = Array.isArray(result?.prospects) ? result.prospects : [];
            const byId = new Map(updatedRows.map((row) => [row._id, row]));
            setProspects((current) => current.map((item) => (byId.has(item._id) ? { ...item, ...byId.get(item._id) } : item)));
            enqueueSnackbar(`Updated ${result?.modified ?? updatedRows.length} prospect(s)`, { variant: 'success' });
            setSelectedProspectRowKeys([]);
            await loadData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to bulk update prospects', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setBulkUpdating(false);
        }
    }, [enqueueSnackbar, loadData, selectedProspectRowKeys]);

    const handleManualProspectFollowUp = useCallback(async (prospect) => {
        if (!prospect?._id) return;
        setManualFollowUpProspectId(prospect._id);
        try {
            const result = await sendManualOutreachFollowUp({ prospectId: prospect._id });
            enqueueSnackbar(`Manual follow-up sent to ${result?.recipient || prospect.email}`, { variant: 'success' });
            await loadData();
            await refreshOperationsData();
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to send manual follow-up', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setManualFollowUpProspectId('');
        }
    }, [enqueueSnackbar, loadData, refreshOperationsData]);

    const handleLoadProspectTimeline = useCallback(async (prospect) => {
        if (!prospect?._id) return;
        if (prospectTimelines[prospect._id]) return;
        setTimelineLoadingProspectId(prospect._id);
        try {
            const result = await getOutreachProspectTimeline({ prospectId: prospect._id });
            setProspectTimelines((current) => ({
                ...current,
                [prospect._id]: Array.isArray(result?.timeline) ? result.timeline : [],
            }));
        } catch (err) {
            const apiMessage = err?.response?.data?.message;
            enqueueSnackbar(apiMessage || 'Failed to load prospect timeline', { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTimelineLoadingProspectId('');
        }
    }, [enqueueSnackbar, prospectTimelines]);

    useEffect(() => {
        if (!isSuperAdmin(role)) return;
        loadData();
        refreshOperationsData();
    }, [loadData, refreshOperationsData, role]);

    useEffect(() => {
        if (!isSuperAdmin(role)) return;
        if (activeOutreachTab === 'advanced') {
            refreshOperationsData();
        }
    }, [activeOutreachTab, refreshOperationsData, role]);

    const summary = useMemo(() => {
        const running = runs.filter((run) => run.status === 'running').length;
        const completed = runs.filter((run) => run.status === 'completed').length;
        const failed = runs.filter((run) => run.status === 'failed').length;
        const totalOpens = engagement.reduce((sum, row) => sum + (row.opens || 0), 0);
        const totalClicks = engagement.reduce((sum, row) => sum + (row.clicks || 0), 0);
        const totalBounces = engagement.reduce((sum, row) => sum + (row.bounces || 0), 0);
        const highIntent = warmLeads.filter((row) => row.interestLevel === 'high-intent').length;
        return { running, completed, failed, totalOpens, totalClicks, totalBounces, highIntent };
    }, [engagement, runs, warmLeads]);

    const priorityLeads = useMemo(() => {
        return [...warmLeads]
            .sort((a, b) => {
                const scoreDiff = Number(b.interestScore || 0) - Number(a.interestScore || 0);
                if (scoreDiff !== 0) return scoreDiff;
                return new Date(b.latestEventAt || 0) - new Date(a.latestEventAt || 0);
            })
            .slice(0, 5);
    }, [warmLeads]);

    const runColumns = [
        {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (value) => <Tag color={statusColor[value] || 'default'}>{value || 'unknown'}</Tag>,
        },
        {
            title: 'Run',
            dataIndex: 'label',
            render: (value, record) => value || record.runId,
        },
        {
            title: 'Area',
            dataIndex: 'countyOrArea',
            width: 150,
            render: (value) => value || '—',
        },
        {
            title: 'Market',
            dataIndex: 'country',
            width: 90,
            render: (value) => (value || 'uk').toUpperCase(),
        },
        {
            title: 'Processed',
            width: 110,
            render: (_, record) => Number(record?.stats?.processed || 0),
        },
        {
            title: 'With Email',
            width: 110,
            render: (_, record) => Number(record?.stats?.withEmail || 0),
        },
        {
            title: 'Failed',
            width: 90,
            render: (_, record) => Number(record?.stats?.failed || 0),
        },
        {
            title: 'Last Update',
            width: 170,
            render: (_, record) => formatDateTime(record.lastHeartbeatAt || record.updatedAt),
        },
        {
            title: 'Seed URL',
            dataIndex: 'seedUrl',
            width: 260,
            ellipsis: true,
            render: (value) => value || '—',
        },
        {
            title: 'Target CSV',
            dataIndex: 'appendTo',
            width: 220,
            ellipsis: true,
            render: (value) => value || '—',
        },
        {
            title: 'Requested By',
            dataIndex: 'requestedByEmail',
            width: 220,
            ellipsis: true,
            render: (value) => value || '—',
        },
        {
            title: 'Action',
            width: 120,
            render: (_, record) => {
                if (record?.status !== 'running') {
                    return '—';
                }

                return (
                    <Button
                        danger
                        size="small"
                        loading={cancellingRunId === record.runId}
                        onClick={() => handleCancelRun(record.runId)}
                    >
                        Cancel
                    </Button>
                );
            },
        },
    ];

    const renderDiscoveryRunDetail = useCallback((record) => {
        const sourceBreakdownRows = Object.entries(record?.stats?.sourceBreakdown || {}).map(([sourceKey, stats]) => ({
            sourceKey,
            seedPages: Number(stats?.seedPages || 0),
            profileLinks: Number(stats?.profileLinks || 0),
        }));
        const emailSignals = record?.stats?.emailSignals || {};
        const runErrors = (Array.isArray(record?.stats?.errors) ? record.stats.errors : []).map((error, index) => ({
            ...error,
            key: `${record.runId}-${String(error?.at || 'unknown')}-${String(error?.scope || 'scope')}-${index}`,
        }));

        return (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Typography.Text type="secondary">Email hits on listing page</Typography.Text>
                            <Typography.Title level={4} style={{ margin: 0 }}>
                                {Number(emailSignals.profilePageHits || 0)}
                            </Typography.Title>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Typography.Text type="secondary">Email hits on installer website</Typography.Text>
                            <Typography.Title level={4} style={{ margin: 0 }}>
                                {Number(emailSignals.websitePageHits || 0)}
                            </Typography.Title>
                        </Card>
                    </Col>
                    <Col xs={24} md={8}>
                        <Card size="small">
                            <Typography.Text type="secondary">Email hits on contact page</Typography.Text>
                            <Typography.Title level={4} style={{ margin: 0 }}>
                                {Number(emailSignals.contactPageHits || 0)}
                            </Typography.Title>
                        </Card>
                    </Col>
                </Row>

                <Card size="small" title="Source yield by directory host">
                    <Table
                        size="small"
                        rowKey="sourceKey"
                        dataSource={sourceBreakdownRows}
                        pagination={false}
                        columns={[
                            { title: 'Source host', dataIndex: 'sourceKey' },
                            { title: 'Seed pages crawled', dataIndex: 'seedPages', width: 180 },
                            { title: 'Profile links discovered', dataIndex: 'profileLinks', width: 220 },
                        ]}
                        locale={{ emptyText: 'No source breakdown captured for this run.' }}
                    />
                </Card>

                <Card size="small" title="Run errors">
                    <Table
                        size="small"
                        rowKey="key"
                        dataSource={runErrors}
                        pagination={false}
                        columns={[
                            { title: 'When', dataIndex: 'at', width: 180, render: formatDateTime },
                            { title: 'Scope', dataIndex: 'scope', width: 120 },
                            { title: 'URL', dataIndex: 'url', render: (value) => value || '—' },
                            { title: 'Message', dataIndex: 'message' },
                        ]}
                        locale={{ emptyText: 'No processing errors recorded.' }}
                    />
                </Card>
            </Space>
        );
    }, []);

    const renderAutomationRunDetails = useCallback((details) => {
        if (!details || typeof details !== 'object' || Array.isArray(details)) {
            return '—';
        }
        const entries = Object.entries(details).filter(([, value]) => value !== null && value !== undefined && value !== '');
        if (entries.length === 0) return '—';
        return (
            <Space wrap size={[4, 4]}>
                {entries.map(([key, value]) => {
                    const label = key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/[_-]+/g, ' ')
                        .replace(/^./, (character) => character.toUpperCase());
                    const renderedValue = typeof value === 'object'
                        ? JSON.stringify(value)
                        : String(value);
                    return (
                        <Tag key={`${key}-${renderedValue}`} style={{ marginInlineEnd: 0 }}>
                            {label}: {renderedValue}
                        </Tag>
                    );
                })}
            </Space>
        );
    }, []);

    const engagementColumns = [
        { title: 'Recipient', dataIndex: 'recipient' },
        { title: 'Opens', dataIndex: 'opens', width: 80 },
        { title: 'Clicks', dataIndex: 'clicks', width: 80 },
        { title: 'Bounces', dataIndex: 'bounces', width: 90 },
        { title: 'Score', dataIndex: 'engagementScore', width: 80 },
        {
            title: 'Interest',
            dataIndex: 'interestLevel',
            width: 110,
            render: (value) => <Tag color={value === 'high-intent' ? 'red' : value === 'warm' ? 'orange' : 'default'}>{value || 'cold'}</Tag>,
        },
        {
            title: 'Latest Event',
            width: 170,
            render: (_, record) => formatDateTime(record.latestEventAt),
        },
        {
            title: 'Subject',
            dataIndex: 'latestSubject',
            render: (value) => value || '—',
        },
        {
            title: 'Outcome',
            width: 220,
            render: (_, record) => (
                <Select
                    aria-label={`Record outcome for ${record.recipient || 'installer lead'}`}
                    placeholder="Record outcome"
                    loading={recordingOutcomeRecipient === record.recipient}
                    onChange={(outcome) => handleRecordOutcome(record.recipient, outcome)}
                    options={[
                        { value: 'qualified_reply', label: 'Qualified reply' },
                        { value: 'consultation_booked', label: 'Consultation booked' },
                        { value: 'consultation_attended', label: 'Consultation attended' },
                        { value: 'quote_created', label: 'Quote created' },
                        { value: 'quote_accepted', label: 'Quote accepted' },
                        { value: 'customer_won', label: 'Customer won' },
                    ]}
                    style={{ width: '100%' }}
                />
            ),
        },
    ];

    const warmLeadColumns = [
        { title: 'Recipient', dataIndex: 'recipient' },
        { title: 'Interest', dataIndex: 'interestLevel', width: 110, render: (value) => <Tag color={value === 'high-intent' ? 'red' : 'orange'}>{value || 'warm'}</Tag> },
        { title: 'Score', dataIndex: 'interestScore', width: 90 },
        { title: 'Opens', dataIndex: 'opens', width: 80 },
        { title: 'Clicks', dataIndex: 'clicks', width: 80 },
        { title: 'Delivered', dataIndex: 'delivered', width: 100 },
        {
            title: 'Contact status',
            width: 150,
            render: (_, record) => {
                if (record.doNotSend) return <Tag color="red">Do not send</Tag>;
                if (record.canContact) return <Tag color="green">Ready to send</Tag>;
                return <Tag color="orange">{record.contactBlockReason || 'Not approved'}</Tag>;
            },
        },
        {
            title: 'Last Event',
            width: 170,
            render: (_, record) => formatDateTime(record.latestEventAt),
        },
        {
            title: 'Subject',
            dataIndex: 'latestSubject',
            render: (value) => value || '—',
        },
        {
            title: 'Action',
            width: 170,
            render: (_, record) => (
                record.doNotSend ? (
                    <Tag color="red">Already blocked</Tag>
                ) : (
                    <Button
                        size="small"
                        loading={markingWarmLeadRecipient === record.recipient}
                        onClick={() => handleWarmLeadDoNotSend(record)}
                    >
                        Mark not to send
                    </Button>
                )
            ),
        },
    ];

    const eventColumns = [
        {
            title: 'Time',
            dataIndex: 'occurredAt',
            width: 170,
            render: formatDateTime,
        },
        {
            title: 'Event',
            dataIndex: 'eventType',
            width: 110,
            render: (value) => <Tag>{value || 'unknown'}</Tag>,
        },
        {
            title: 'Recipient',
            dataIndex: 'recipient',
            width: 220,
            render: (value) => value || '—',
        },
        {
            title: 'From',
            width: 220,
            render: (_, record) => record.fromAddress || record.sender || '—',
        },
        {
            title: 'Subject',
            dataIndex: 'subject',
            width: 280,
            render: (value) => value || '—',
        },
        {
            title: 'Bounce',
            dataIndex: 'bounceType',
            width: 100,
            render: (value) => value || '—',
        },
        {
            title: 'Delivery detail',
            dataIndex: 'message',
            width: 320,
            render: (value) => value || '—',
        },
        {
            title: 'Email ID',
            dataIndex: 'emailId',
            width: 180,
            render: (value) => value || '—',
        },
        {
            title: 'Message ID',
            dataIndex: 'messageId',
            width: 220,
            render: (value) => value || '—',
        },
        {
            title: 'Webhook ID',
            dataIndex: 'webhookId',
            width: 220,
            render: (value) => value || '—',
        },
        {
            title: 'Client',
            width: 200,
            render: (_, record) => {
                const parts = [record.client, record.clientDevice, record.clientOs].filter(Boolean);
                return parts.length > 0 ? parts.join(' / ') : '—';
            },
        },
        {
            title: 'Country',
            dataIndex: 'country',
            width: 110,
            render: (value) => value || '—',
        },
    ];

    const prospectColumns = [
        {
            title: 'Company',
            dataIndex: 'companyName',
            render: (value) => value || '—',
        },
        {
            title: 'Area',
            dataIndex: 'countyOrArea',
            width: 140,
            render: (value) => value || '—',
        },
        {
            title: 'Market',
            dataIndex: 'country',
            width: 90,
            render: (value) => (value || 'uk').toUpperCase(),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            render: (value) => value || '—',
        },
        {
            title: 'Website',
            dataIndex: 'website',
            render: (value) => value || '—',
        },
        {
            title: 'Marketing basis',
            dataIndex: 'marketingBasis',
            width: 240,
            render: (value, record) => (
                <Select
                    aria-label={`Marketing basis for ${record.companyName || 'prospect'}`}
                    value={value || 'unverified'}
                    loading={updatingProspectId === record._id}
                    onChange={(marketingBasis) => handleProspectMarketingBasisChange(record, marketingBasis)}
                    options={[
                        { value: 'unverified', label: 'Unverified - do not send' },
                        { value: 'documented_legitimate_interest', label: 'Legitimate customer prospect' },
                        { value: 'explicit_consent', label: 'Explicit consent' },
                    ]}
                    style={{ width: '100%' }}
                />
            ),
        },
        {
            title: 'Updated',
            dataIndex: 'updatedAt',
            width: 160,
            render: formatDateTime,
        },
        {
            title: 'Do not send',
            dataIndex: 'doNotSend',
            width: 130,
            render: (value, record) => (
                <Switch
                    checked={value === true}
                    loading={updatingProspectId === record._id}
                    onChange={(checked) => handleProspectDoNotSendChange(record, checked)}
                    aria-label={`Do not send outreach to ${record.companyName || 'prospect'}`}
                />
            ),
        },
        {
            title: 'Why not contacted',
            dataIndex: 'outreachStateReason',
            width: 190,
            render: (value) => {
                if (!value) return '—';
                const color = value === 'ready-to-contact'
                    ? 'green'
                    : value === 'contacted'
                        ? 'blue'
                        : value === 'do-not-send'
                            ? 'red'
                            : 'orange';
                return <Tag color={color}>{value}</Tag>;
            },
        },
        {
            title: 'Prospect approval',
            width: 220,
            render: (_, record) => (
                <Button
                    size="small"
                    type={record.marketingBasis === 'documented_legitimate_interest' && record.doNotSend !== true ? 'default' : 'primary'}
                    disabled={record.marketingBasis === 'documented_legitimate_interest' && record.doNotSend !== true}
                    loading={updatingProspectId === record._id}
                    onClick={() => handleMarkProspectLegitimate(record)}
                >
                    {record.marketingBasis === 'documented_legitimate_interest' && record.doNotSend !== true
                        ? 'Approved legitimate prospect'
                        : 'Mark as legitimate prospect'}
                </Button>
            ),
        },
        {
            title: 'Manual send',
            width: 190,
            render: (_, record) => (
                <Button
                    size="small"
                    disabled={!record.canContact}
                    loading={manualFollowUpProspectId === record._id}
                    onClick={() => handleManualProspectFollowUp(record)}
                >
                    Send follow-up now
                </Button>
            ),
        },
    ];

    if (!isSuperAdmin(role)) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Typography.Title level={4}>Outreach Monitor</Typography.Title>
                <Typography.Text type="secondary">Super admin access is required.</Typography.Text>
            </PageLayout>
        );
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Typography.Title level={2} style={{ margin: 0 }}>Outreach Monitor</Typography.Title>
                    <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Refresh</Button>
                </Space>

                <Card title="Email Preview Tests" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Typography.Text type="secondary">
                            Send the live first-touch and warm-lead follow-up templates to a personal inbox so you can review exactly what prospects receive.
                        </Typography.Text>
                        <Input
                            value={previewRecipient}
                            onChange={(event) => setPreviewRecipient(event.target.value)}
                            placeholder="you@example.com"
                            style={{ maxWidth: 420 }}
                        />
                        <Space>
                            <Button icon={<EyeOutlined />} loading={previewLoading} onClick={handleViewEmailPreviews}>
                                View email previews
                            </Button>
                            <Button type="primary" loading={previewSending} onClick={handlePreviewEmails}>
                                Send test emails to me
                            </Button>
                            {previewResult ? (
                                <Typography.Text type="secondary">
                                    Sent {previewResult.sent ?? 0} of {previewResult.total ?? 0} variants.
                                </Typography.Text>
                            ) : null}
                        </Space>
                    </Space>
                </Card>

                <Modal
                    title="Outreach email previews"
                    open={previewModalOpen}
                    onCancel={() => setPreviewModalOpen(false)}
                    footer={null}
                    width={820}
                    destroyOnHidden
                >
                    <Tabs
                        items={previewVariants.map((variant) => ({
                            key: variant.key,
                            label: variant.key.startsWith('installpath-') ? `InstallPath: ${variant.key.replace('installpath-', '')}` : `Solar Calc: ${variant.key}`,
                            children: (
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <div>
                                        <Typography.Text strong>Subject: </Typography.Text>
                                        <Typography.Text>{variant.subject}</Typography.Text>
                                    </div>
                                    <iframe
                                        title={`${variant.key} email preview`}
                                        srcDoc={variant.html}
                                        style={{ width: '100%', height: 520, border: '1px solid #d9d9d9', background: '#fff' }}
                                    />
                                    <details>
                                        <summary>View plain-text version</summary>
                                        <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{variant.text}</pre>
                                    </details>
                                </Space>
                            ),
                        }))}
                    />
                </Modal>

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">Runs Running</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.running}</Typography.Title></Card></Col>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">Runs Completed</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.completed}</Typography.Title></Card></Col>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">Runs Failed</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.failed}</Typography.Title></Card></Col>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">Outreach Opens</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.totalOpens}</Typography.Title></Card></Col>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">Outreach Clicks</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.totalClicks}</Typography.Title></Card></Col>
                    <Col xs={12} md={4}><Card><Typography.Text type="secondary">High Intent</Typography.Text><Typography.Title level={3} style={{ margin: 0 }}>{summary.highIntent}</Typography.Title></Card></Col>
                </Row>

                <Card
                    title="Solar Calc Outreach Safety & Pipeline (last 30 days)"
                    style={{ marginBottom: 16 }}
                    extra={performance?.deliverySafety?.paused ? <Tag color="error">Follow-ups paused</Tag> : <Tag color="success">Delivery gate clear</Tag>}
                >
                    <Typography.Paragraph type="secondary">
                        Follow-up safety uses permanent-bounce and complaint signals. Opens are not a primary conversion metric.
                    </Typography.Paragraph>
                    <Row gutter={[16, 16]}>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Delivered</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.deliverySafety?.delivered ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Permanent bounces</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.deliverySafety?.permanentBounced ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Bounce rate</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance ? `${(Number(performance.deliverySafety?.permanentBounceRate || 0) * 100).toFixed(1)}%` : '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Qualified replies</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.outcomes?.filter((item) => item.outcome === 'qualified_reply').reduce((total, item) => total + item.count, 0) ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Consultations booked</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.outcomes?.filter((item) => item.outcome === 'consultation_booked').reduce((total, item) => total + item.count, 0) ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Customers won</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.outcomes?.filter((item) => item.outcome === 'customer_won').reduce((total, item) => total + item.count, 0) ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Funnel discovered</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.funnel?.discoveredProspects ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Funnel approved</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.funnel?.approvedProspects ?? '—'}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Funnel contacted</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{performance?.funnel?.contactedProspects ?? '—'}</Typography.Title></Col>
                    </Row>
                </Card>

                <Tabs
                    activeKey={activeOutreachTab}
                    onChange={setActiveOutreachTab}
                    items={[
                        {
                            key: 'overview',
                            label: 'Overview',
                            children: (
                                <>
                                    <Card title="Priority Queue" style={{ marginBottom: 16 }}>
                                        <Space wrap style={{ marginBottom: 16 }}>
                                            <Space size={4}>
                                                <Typography.Text type="secondary">Min score</Typography.Text>
                                                <InputNumber min={1} max={100} value={followUpMinimumScore} onChange={(value) => setFollowUpMinimumScore(value || 40)} style={{ width: 80 }} />
                                            </Space>
                                            <Space size={4}>
                                                <Typography.Text type="secondary">Max sends</Typography.Text>
                                                <InputNumber min={1} max={50} value={followUpLimit} onChange={(value) => setFollowUpLimit(value || 20)} style={{ width: 80 }} />
                                            </Space>
                                            <Space size={4}>
                                                <Typography.Text type="secondary">Window (days)</Typography.Text>
                                                <InputNumber min={1} max={90} value={followUpWindowDays} onChange={(value) => setFollowUpWindowDays(value || 7)} style={{ width: 80 }} />
                                            </Space>
                                            <Button loading={followUpSending} onClick={() => handleWarmLeadFollowUps(true)}>
                                                Preview Follow-Ups
                                            </Button>
                                            <Popconfirm
                                                title="Send live follow-up emails?"
                                                description={`Sends up to ${followUpLimit} real emails to warm leads scoring ${followUpMinimumScore}+.`}
                                                okText="Send"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleWarmLeadFollowUps(false)}
                                            >
                                                <Button type="primary" danger loading={followUpSending}>
                                                    Send Follow-Ups
                                                </Button>
                                            </Popconfirm>
                                        </Space>
                                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                                            <Col xs={12} md={6}><Typography.Text type="secondary">Warm Leads</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{warmLeads.length || 0}</Typography.Title></Col>
                                            <Col xs={12} md={6}><Typography.Text type="secondary">High Intent</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{summary.highIntent}</Typography.Title></Col>
                                            <Col xs={12} md={6}><Typography.Text type="secondary">Running</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{summary.running}</Typography.Title></Col>
                                            <Col xs={12} md={6}><Typography.Text type="secondary">Bounced</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{summary.totalBounces}</Typography.Title></Col>
                                        </Row>
                                        {followUpResult ? (
                                            followUpResult.dryRun ? (
                                                <div style={{ marginBottom: 12 }}>
                                                    <Typography.Text type="secondary">Preview:</Typography.Text>{' '}
                                                    <Typography.Text strong>{followUpResult.toSend?.length ?? 0}</Typography.Text> would be sent{' '}
                                                    ({followUpResult.totalCandidates ?? 0} candidates, {followUpResult.skipped?.length ?? 0} skipped).
                                                    {Array.isArray(followUpResult.toSend) && followUpResult.toSend.length > 0 ? (
                                                        <div style={{ marginTop: 8 }}>
                                                            {followUpResult.toSend.map((lead) => (
                                                                <Tag key={lead.recipient} color="orange">{lead.recipient}</Tag>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <div style={{ marginBottom: 12 }}>
                                                    <Typography.Text type="secondary">Last send summary:</Typography.Text>{' '}
                                                    <Typography.Text strong>{followUpResult.sent ?? 0}</Typography.Text> sent,{' '}
                                                    <Typography.Text strong>{followUpResult.failed ?? 0}</Typography.Text> failed.
                                                </div>
                                            )
                                        ) : null}
                                        <Table
                                            size="small"
                                            bordered
                                            dataSource={priorityLeads}
                                            columns={[
                                                { title: 'Recipient', dataIndex: 'recipient', render: (value) => value || '—' },
                                                { title: 'Interest', dataIndex: 'interestLevel', width: 120, render: (value) => <Tag color={value === 'high-intent' ? 'red' : value === 'warm' ? 'orange' : 'default'}>{value || 'warm'}</Tag> },
                                                { title: 'Score', dataIndex: 'interestScore', width: 90 },
                                                {
                                                    title: 'Contact status',
                                                    width: 150,
                                                    render: (_, record) => {
                                                        if (record.doNotSend) return <Tag color="red">Do not send</Tag>;
                                                        if (record.canContact) return <Tag color="green">Ready to send</Tag>;
                                                        return <Tag color="orange">{record.contactBlockReason || 'Not approved'}</Tag>;
                                                    },
                                                },
                                                { title: 'Last Event', width: 170, render: (_, record) => formatDateTime(record.latestEventAt) },
                                                {
                                                    title: 'Next Step',
                                                    width: 160,
                                                    render: (_, record) => {
                                                        if (record.doNotSend) return 'Blocked';
                                                        return record.interestLevel === 'high-intent' ? 'Manual follow-up' : 'Standard follow-up';
                                                    },
                                                },
                                                {
                                                    title: 'Action',
                                                    width: 170,
                                                    render: (_, record) => (
                                                        record.doNotSend ? (
                                                            <Tag color="red">Already blocked</Tag>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                loading={markingWarmLeadRecipient === record.recipient}
                                                                onClick={() => handleWarmLeadDoNotSend(record)}
                                                            >
                                                                Mark not to send
                                                            </Button>
                                                        )
                                                    ),
                                                },
                                            ]}
                                            rowKey={(record) => record.recipient}
                                            pagination={false}
                                            locale={{ emptyText: 'No warm leads at the moment.' }}
                                        />
                                    </Card>

                                    <Card title="Warm Leads" style={{ marginBottom: 16 }}>
                                        <Table
                                            size="small"
                                            bordered
                                            dataSource={warmLeads}
                                            columns={warmLeadColumns}
                                            rowKey={(record) => record.recipient}
                                            pagination={{ pageSize: 10 }}
                                            locale={{ emptyText: 'No warm leads yet.' }}
                                        />
                                    </Card>
                                </>
                            ),
                        },
                        {
                            key: 'advanced',
                            label: 'Advanced',
                            children: (
                                <>
                                    <Card title="Scheduler Endpoint Controls" style={{ marginBottom: 16 }}>
                                        <Typography.Paragraph type="secondary">
                                            These controls call the same server endpoints used by Cloud Scheduler.
                                        </Typography.Paragraph>
                                        <Space wrap style={{ marginBottom: 12 }}>
                                            <Button loading={operationsLoading} onClick={refreshOperationsData}>
                                                Refresh operations log
                                            </Button>
                                            <Button
                                                loading={retryingFailedFollowUps}
                                                onClick={handleRetryFailedFollowUps}
                                            >
                                                Retry failed follow-ups (7 days)
                                            </Button>
                                            <Button
                                                danger={deliveryControl?.mode !== 'paused'}
                                                onClick={() => handleDeliveryControlUpdate(deliveryControl?.mode === 'paused' ? 'auto' : 'paused')}
                                                loading={operationsLoading}
                                            >
                                                {deliveryControl?.mode === 'paused' ? 'Resume automated follow-ups' : 'Pause automated follow-ups'}
                                            </Button>
                                        </Space>
                                        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                                            <Col xs={24} md={8}>
                                                <Typography.Text type="secondary">Manual control</Typography.Text>
                                                <Typography.Title level={4} style={{ margin: 0 }}>
                                                    {deliveryControl?.mode === 'paused' ? 'Paused' : 'Auto'}
                                                </Typography.Title>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Typography.Text type="secondary">Updated</Typography.Text>
                                                <Typography.Title level={5} style={{ margin: 0 }}>
                                                    {formatDateTime(deliveryControl?.updatedAt)}
                                                </Typography.Title>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Typography.Text type="secondary">Actor</Typography.Text>
                                                <Typography.Title level={5} style={{ margin: 0 }}>
                                                    {deliveryControl?.actor || '—'}
                                                </Typography.Title>
                                            </Col>
                                        </Row>
                                        <Space wrap style={{ marginBottom: 12 }}>
                                            <Button
                                                loading={schedulerActionLoading === 'run-next'}
                                                onClick={() => handleSchedulerAction('run-next')}
                                            >
                                                Run discovery scheduler step
                                            </Button>
                                            <Button
                                                loading={schedulerActionLoading === 'process-batch'}
                                                onClick={() => handleSchedulerAction('process-batch')}
                                            >
                                                Process discovery batch
                                            </Button>
                                            <Button
                                                loading={schedulerActionLoading === 'run-follow-ups'}
                                                onClick={() => handleSchedulerAction('run-follow-ups')}
                                            >
                                                Run scheduled follow-ups
                                            </Button>
                                            <Button
                                                loading={schedulerActionLoading === 'view-approved-prospects'}
                                                onClick={() => handleSchedulerAction('view-approved-prospects')}
                                            >
                                                View approved prospects
                                            </Button>
                                            <Button
                                                loading={schedulerActionLoading === 'view-suppressions'}
                                                onClick={() => handleSchedulerAction('view-suppressions')}
                                            >
                                                View suppression hashes
                                            </Button>
                                        </Space>
                                        {schedulerActionResult ? (
                                            <Card size="small" type="inner" title="Last scheduler endpoint result">
                                                <Typography.Text type="secondary">Action:</Typography.Text>{' '}
                                                <Typography.Text>{schedulerActionResult.action}</Typography.Text>
                                                {(schedulerActionResult.action === 'view-approved-prospects' || schedulerActionResult.action === 'view-suppressions') ? (
                                                    <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                                                        Use the tables below to inspect the loaded dataset.
                                                    </Typography.Text>
                                                ) : null}
                                                <pre style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                                                    {JSON.stringify(schedulerActionResult.result, null, 2)}
                                                </pre>
                                            </Card>
                                        ) : null}
                                    </Card>

                                    <Card title="Automation Run Log" style={{ marginBottom: 16 }}>
                                        <Table
                                            size="small"
                                            rowKey={(record, index) => `${record.timestamp || 'na'}-${record.operation}-${index}`}
                                            dataSource={automationRuns}
                                            pagination={{ pageSize: 8 }}
                                            columns={[
                                                { title: 'Time', dataIndex: 'timestamp', width: 170, render: formatDateTime },
                                                { title: 'Operation', dataIndex: 'operation', width: 260, render: (value) => value || '—' },
                                                { title: 'Status', dataIndex: 'status', width: 120, render: (value) => <Tag>{value || 'unknown'}</Tag> },
                                                { title: 'Reason', dataIndex: 'reason', width: 260, render: (value) => value || '—' },
                                                { title: 'Details', dataIndex: 'details', width: 420, render: renderAutomationRunDetails },
                                                { title: 'Actor', dataIndex: 'actor', width: 220, render: (value) => value || '—' },
                                                { title: 'Scheduler', dataIndex: 'schedulerAuthorized', width: 120, render: (value) => value ? 'yes' : 'no' },
                                            ]}
                                            scroll={{ x: 1650 }}
                                            locale={{ emptyText: 'No automation run records yet.' }}
                                        />
                                    </Card>

                                    <Card title="Approved Prospect Export View" style={{ marginBottom: 16 }}>
                                        <Typography.Text type="secondary">
                                            These are currently approved prospects eligible for outreach export.
                                        </Typography.Text>
                                        <Table
                                            size="small"
                                            style={{ marginTop: 12 }}
                                            rowKey={(record) => `${record.email}-${record.source_url || 'source'}`}
                                            dataSource={approvedProspectsPreview}
                                            columns={[
                                                { title: 'Company', dataIndex: 'company_name', render: (value) => value || '—' },
                                                { title: 'Region', dataIndex: 'county_or_area', width: 140, render: (value) => value || '—' },
                                                { title: 'Email', dataIndex: 'email', render: (value) => value || '—' },
                                                { title: 'Website', dataIndex: 'website', render: (value) => value || '—' },
                                                { title: 'Consent Basis', dataIndex: 'consent_opt_in', width: 240, render: (value) => value || '—' },
                                            ]}
                                            pagination={{ pageSize: 8 }}
                                            scroll={{ x: 980 }}
                                            locale={{ emptyText: 'Run "View approved prospects" to load data.' }}
                                        />
                                    </Card>

                                    <Card title="Suppression Hashes" style={{ marginBottom: 16 }}>
                                        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                                            <Col xs={24} md={8}>
                                                <Typography.Text type="secondary">Hashes loaded</Typography.Text>
                                                <Typography.Title level={4} style={{ margin: 0 }}>{suppressionHashesPreview.length}</Typography.Title>
                                            </Col>
                                        </Row>
                                        {suppressionDetailRows.length > 0 ? (
                                            <Table
                                                size="small"
                                                rowKey={(record, index) => `${record.recipientHash}-${index}`}
                                                dataSource={suppressionDetailRows}
                                                columns={[
                                                    { title: 'Recipient hash', dataIndex: 'recipientHash', width: 280 },
                                                    { title: 'Reason', dataIndex: 'reason', width: 160, render: (value) => value || '—' },
                                                    { title: 'First seen', dataIndex: 'firstSeenAt', width: 170, render: formatDateTime },
                                                    { title: 'Last seen', dataIndex: 'lastSeenAt', width: 170, render: formatDateTime },
                                                    { title: 'Last event', dataIndex: 'lastEventType', width: 130, render: (value) => value || '—' },
                                                    { title: 'Bounce', dataIndex: 'bounceType', width: 130, render: (value) => value || '—' },
                                                    { title: 'Source', dataIndex: 'source', width: 220, render: (value) => value || '—' },
                                                    { title: 'Message', dataIndex: 'message', width: 320, render: (value) => value || '—' },
                                                ]}
                                                pagination={{ pageSize: 8 }}
                                                scroll={{ x: 1700 }}
                                            />
                                        ) : (
                                            <Table
                                                size="small"
                                                rowKey={(value) => value}
                                                dataSource={suppressionHashesPreview}
                                                columns={[
                                                    { title: 'SHA-256 hash', render: (_, record) => record },
                                                ]}
                                                pagination={{ pageSize: 10 }}
                                                locale={{ emptyText: 'Run "View suppression hashes" to load data.' }}
                                            />
                                        )}
                                    </Card>

                                    <Card title="Outreach Diagnostics Test" style={{ marginBottom: 16 }}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Space>
                                                <Button onClick={handleDiagnosticsCheck} loading={diagnosticsLoading}>
                                                    Run Diagnostics Check
                                                </Button>
                                                <Typography.Text type="secondary">
                                                    Confirms whether this admin environment is using the dedicated outreach DB target.
                                                </Typography.Text>
                                            </Space>
                                            {diagnosticsResult ? (
                                                <Row gutter={[12, 8]}>
                                                    <Col xs={24} md={8}><Typography.Text strong>Storage Mode:</Typography.Text> <Typography.Text>{diagnosticsResult.outreachStorageMode || '—'}</Typography.Text></Col>
                                                    <Col xs={24} md={8}><Typography.Text strong>Dedicated DB:</Typography.Text> <Typography.Text>{diagnosticsResult.usingDedicatedOutreachDb ? 'yes' : 'no'}</Typography.Text></Col>
                                                    <Col xs={24} md={8}><Typography.Text strong>DB Target:</Typography.Text> <Typography.Text>{diagnosticsResult.outreachDbTarget || '—'}</Typography.Text></Col>
                                                    <Col xs={24} md={8}><Typography.Text strong>Runs:</Typography.Text> <Typography.Text>{diagnosticsResult?.counts?.discoveryRuns ?? '—'}</Typography.Text></Col>
                                                    <Col xs={24} md={8}><Typography.Text strong>Prospects:</Typography.Text> <Typography.Text>{diagnosticsResult?.counts?.prospects ?? '—'}</Typography.Text></Col>
                                                    <Col xs={24} md={8}><Typography.Text strong>Email Events:</Typography.Text> <Typography.Text>{diagnosticsResult?.counts?.emailEvents ?? '—'}</Typography.Text></Col>
                                                    <Col xs={24}><Typography.Text strong>Latest Run:</Typography.Text> <Typography.Text>{diagnosticsResult?.latest?.run?.runId || '—'}</Typography.Text></Col>
                                                    <Col xs={24}><Typography.Text strong>Latest Prospect Activity:</Typography.Text> <Typography.Text>{formatDateTime(diagnosticsResult?.latest?.prospect?.updatedAt)}</Typography.Text></Col>
                                                </Row>
                                            ) : null}
                                        </Space>
                                    </Card>

                                    <Card title="Run Discovery" style={{ marginBottom: 16 }}>
                                        <Row gutter={[12, 12]}>
                                            <Col xs={24} md={6}>
                                                <Typography.Text type="secondary">Market</Typography.Text>
                                                <Select
                                                    value={launchCountry}
                                                    style={{ width: '100%' }}
                                                    onChange={setLaunchCountry}
                                                    options={[
                                                        { value: 'uk', label: 'UK (County)' },
                                                        { value: 'us', label: 'US (State)' },
                                                        { value: 'de', label: 'DE (Province)' },
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={6}>
                                                <Typography.Text type="secondary">Region</Typography.Text>
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder={regionPlaceholderByCountry[launchCountry]}
                                                    value={launchRegion}
                                                    onChange={(value) => setLaunchRegion(value || '')}
                                                    options={regionSelectOptions}
                                                    filterOption={(input, option) => {
                                                        return String(option?.label || '').toLowerCase().includes(input.toLowerCase());
                                                    }}
                                                    style={{ width: '100%' }}
                                                />
                                                <Input
                                                    style={{ marginTop: 8 }}
                                                    placeholder="Optional custom region override"
                                                    value={launchCustomRegion}
                                                    onChange={(event) => setLaunchCustomRegion(event.target.value)}
                                                />
                                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                    {regionSelectOptions.length} preset options. If missing, type a custom region above.
                                                </Typography.Text>
                                            </Col>
                                            <Col xs={24} md={6}>
                                                <Typography.Text type="secondary">Run Label (Optional)</Typography.Text>
                                                <Input
                                                    placeholder="Custom run label"
                                                    value={launchLabel}
                                                    onChange={(event) => setLaunchLabel(event.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Typography.Text type="secondary">Seed URLs (Optional, one per line)</Typography.Text>
                                                <Input.TextArea
                                                    rows={4}
                                                    placeholder={'Leave blank to use default sources. Example:\nhttps://solarinstallerlist.com/solar-installers/uk/south-east\nhttps://www.yell.com/s/solar+energy+installers-south+east.html'}
                                                    value={launchSeedUrlsInput}
                                                    onChange={(event) => setLaunchSeedUrlsInput(event.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Typography.Text type="secondary">Listing URL Patterns (Optional, one per line)</Typography.Text>
                                                <Input.TextArea
                                                    rows={4}
                                                    placeholder={'Regex patterns for profile links. Example:\nsolarinstallerlist\\.com/solar-installers/uk/\nyell\\.com/biz/\ncheckatrade\\.com/trades/'}
                                                    value={launchListingPatternsInput}
                                                    onChange={(event) => setLaunchListingPatternsInput(event.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Typography.Text type="secondary">Target CSV (Optional Override)</Typography.Text>
                                                <Input
                                                    placeholder={defaultAppendTargets[launchCountry]}
                                                    value={launchAppendTo}
                                                    onChange={(event) => setLaunchAppendTo(event.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24}>
                                                <Space>
                                                    <Button type="primary" onClick={handleLaunchDiscovery} loading={launchingDiscovery}>
                                                        Run Discovery
                                                    </Button>
                                                    <Typography.Text type="secondary">
                                                        This appends deduped email-ready rows and updates progress automatically.
                                                    </Typography.Text>
                                                </Space>
                                            </Col>
                                        </Row>
                                    </Card>

                                    <Card title="Suppression Cleanup" style={{ marginBottom: 16 }}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Typography.Text type="secondary">
                                                Uses SMTP2GO events to remove unsubscribe and hard-bounce recipients from outreach CSV files and live DB prospects.
                                            </Typography.Text>
                                            <Input
                                                placeholder="Optional CSV paths, comma-separated (leave blank for defaults)"
                                                value={cleanupCsvPathsInput}
                                                onChange={(event) => setCleanupCsvPathsInput(event.target.value)}
                                            />
                                            <Space>
                                                <Button loading={cleanupLoading} onClick={() => handleSuppressionCleanup(true)}>
                                                    Preview Cleanup
                                                </Button>
                                                <Popconfirm
                                                    title="Apply suppression cleanup?"
                                                    description="Removes unsubscribe and hard-bounce recipients from outreach CSV files and live DB prospects."
                                                    okText="Apply"
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() => handleSuppressionCleanup(false)}
                                                >
                                                    <Button danger loading={cleanupLoading}>
                                                        Apply Cleanup
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                            {cleanupResult ? (
                                                <Card size="small" title={cleanupResult.dryRun ? 'Preview Result' : 'Cleanup Result'}>
                                                    <Row gutter={[12, 8]}>
                                                        <Col xs={24} md={8}><Typography.Text strong>Suppressed Emails:</Typography.Text> <Typography.Text>{cleanupResult.suppressedEmailCount ?? 0}</Typography.Text></Col>
                                                        <Col xs={24} md={8}><Typography.Text strong>Rows Scanned:</Typography.Text> <Typography.Text>{cleanupResult.totalRowsScanned ?? 0}</Typography.Text></Col>
                                                        <Col xs={24} md={8}><Typography.Text strong>Rows Removed:</Typography.Text> <Typography.Text>{cleanupResult.totalRemovedRows ?? 0}</Typography.Text></Col>
                                                        <Col xs={24} md={8}><Typography.Text strong>DB Suppressed:</Typography.Text> <Typography.Text>{cleanupResult.dbRemovedCount ?? 0}</Typography.Text></Col>
                                                    </Row>
                                                    <Table
                                                        style={{ marginTop: 12 }}
                                                        rowKey={(record) => record.csvPath}
                                                        dataSource={Array.isArray(cleanupResult.files) ? cleanupResult.files : []}
                                                        columns={[
                                                            { title: 'CSV Path', dataIndex: 'csvPath' },
                                                            { title: 'Scanned', dataIndex: 'scannedRows', width: 100, render: (value) => value ?? '—' },
                                                            { title: 'Removed', dataIndex: 'removedRows', width: 100, render: (value) => value ?? '—' },
                                                            { title: 'Remaining', dataIndex: 'remainingRows', width: 110, render: (value) => value ?? '—' },
                                                            { title: 'Error', dataIndex: 'error', render: (value) => value || '—' },
                                                        ]}
                                                        pagination={false}
                                                        size="small"
                                                        scroll={{ x: 900 }}
                                                    />
                                                </Card>
                                            ) : null}
                                        </Space>
                                    </Card>

                                    {lastLaunchResult ? (
                                        <Card title="Last Launch" style={{ marginBottom: 16 }}>
                                            <Row gutter={[12, 8]}>
                                                <Col xs={24} md={8}><Typography.Text strong>Run ID:</Typography.Text> <Typography.Text>{lastLaunchResult.runId || '—'}</Typography.Text></Col>
                                                <Col xs={24} md={8}><Typography.Text strong>Market:</Typography.Text> <Typography.Text>{(lastLaunchResult.country || 'uk').toUpperCase()}</Typography.Text></Col>
                                                <Col xs={24} md={8}><Typography.Text strong>Region:</Typography.Text> <Typography.Text>{lastLaunchResult.region || '—'}</Typography.Text></Col>
                                                <Col xs={24}><Typography.Text strong>Seed URL:</Typography.Text> <Typography.Text>{lastLaunchResult.seedUrl || '—'}</Typography.Text></Col>
                                                <Col xs={24}><Typography.Text strong>Seed URLs:</Typography.Text> <Typography.Text>{Array.isArray(lastLaunchResult.seedUrls) && lastLaunchResult.seedUrls.length > 0 ? lastLaunchResult.seedUrls.join(', ') : '—'}</Typography.Text></Col>
                                                <Col xs={24}><Typography.Text strong>Patterns:</Typography.Text> <Typography.Text>{Array.isArray(lastLaunchResult.listingLinkPatterns) && lastLaunchResult.listingLinkPatterns.length > 0 ? lastLaunchResult.listingLinkPatterns.join(', ') : '—'}</Typography.Text></Col>
                                                <Col xs={24}><Typography.Text strong>Target CSV:</Typography.Text> <Typography.Text>{lastLaunchResult.appendTo || '—'}</Typography.Text></Col>
                                            </Row>
                                        </Card>
                                    ) : null}

                                    <Card title="Discovery Runs" style={{ marginBottom: 16 }}>
                                        {loading ? <Spin /> : (
                                            <Table
                                                rowKey={(record) => record.runId}
                                                dataSource={runs}
                                                columns={runColumns}
                                                expandable={{
                                                    expandedRowRender: renderDiscoveryRunDetail,
                                                }}
                                                pagination={{ pageSize: 10 }}
                                                scroll={{ x: 1500 }}
                                            />
                                        )}
                                    </Card>

                                    <Card
                                        title="Live Outreach Prospects (DB)"
                                        style={{ marginBottom: 16 }}
                                        extra={(
                                            <Space>
                                                <Select
                                                    style={{ width: 130 }}
                                                    value={prospectFilters.country}
                                                    onChange={(value) => setProspectFilters((current) => ({ ...current, country: value }))}
                                                    options={[
                                                        { value: '', label: 'All markets' },
                                                        { value: 'uk', label: 'UK' },
                                                        { value: 'us', label: 'US' },
                                                        { value: 'de', label: 'DE' },
                                                    ]}
                                                />
                                                <Input
                                                    placeholder="Search company or email"
                                                    value={prospectSearchDraft}
                                                    onChange={(event) => setProspectSearchDraft(event.target.value)}
                                                    onPressEnter={() => setProspectFilters((current) => ({ ...current, search: prospectSearchDraft.trim() }))}
                                                    style={{ width: 220 }}
                                                    allowClear
                                                />
                                                <Button onClick={() => setProspectFilters((current) => ({ ...current, search: prospectSearchDraft.trim() }))}>Apply</Button>
                                            </Space>
                                        )}
                                    >
                                        <Typography.Text type="secondary">
                                            Use "Mark as legitimate prospect" to approve a business lead for outreach and remove do-not-send in one action.
                                        </Typography.Text>
                                        <Space wrap style={{ marginTop: 12 }}>
                                            <Button
                                                loading={bulkUpdating}
                                                disabled={selectedProspectRowKeys.length === 0}
                                                onClick={() => handleBulkProspectUpdate({
                                                    marketingBasis: 'documented_legitimate_interest',
                                                    doNotSend: false,
                                                })}
                                            >
                                                Bulk allow contact
                                            </Button>
                                            <Button
                                                loading={bulkUpdating}
                                                disabled={selectedProspectRowKeys.length === 0}
                                                onClick={() => handleBulkProspectUpdate({ doNotSend: true })}
                                            >
                                                Bulk block contact
                                            </Button>
                                            <Typography.Text type="secondary">
                                                Selected: {selectedProspectRowKeys.length}
                                            </Typography.Text>
                                        </Space>
                                        {loading ? <Spin /> : (
                                            <Table
                                                style={{ marginTop: 12 }}
                                                rowKey={(record) => record._id}
                                                dataSource={prospects}
                                                columns={prospectColumns}
                                                rowSelection={{
                                                    selectedRowKeys: selectedProspectRowKeys,
                                                    onChange: (keys) => setSelectedProspectRowKeys(keys),
                                                }}
                                                expandable={{
                                                    expandedRowRender: (record) => {
                                                        const timelineRows = prospectTimelines[record._id] || [];
                                                        return (
                                                            <div>
                                                                <Space style={{ marginBottom: 8 }}>
                                                                    <Button
                                                                        size="small"
                                                                        loading={timelineLoadingProspectId === record._id}
                                                                        onClick={() => handleLoadProspectTimeline(record)}
                                                                    >
                                                                        Load timeline
                                                                    </Button>
                                                                </Space>
                                                                <Table
                                                                    size="small"
                                                                    rowKey={(item, index) => `${record._id}-${item.type}-${index}`}
                                                                    dataSource={timelineRows}
                                                                    pagination={false}
                                                                    columns={[
                                                                        { title: 'When', dataIndex: 'at', width: 180, render: formatDateTime },
                                                                        { title: 'Event', dataIndex: 'type', width: 220 },
                                                                        { title: 'Detail', dataIndex: 'detail' },
                                                                    ]}
                                                                    locale={{ emptyText: 'No timeline loaded yet.' }}
                                                                />
                                                            </div>
                                                        );
                                                    },
                                                }}
                                                pagination={{ pageSize: 12 }}
                                                scroll={{ x: 1200 }}
                                            />
                                        )}
                                    </Card>

                                    <Card title="Engaged Recipients" style={{ marginBottom: 16 }}>
                                        {loading ? <Spin /> : (
                                            <Table
                                                rowKey={(record) => record.recipient}
                                                dataSource={engagement}
                                                columns={engagementColumns}
                                                pagination={{ pageSize: 10 }}
                                                scroll={{ x: 1000 }}
                                            />
                                        )}
                                    </Card>

                                    <Card
                                        title="SMTP2GO Events"
                                        extra={(
                                            <Space>
                                                <Select
                                                    allowClear
                                                    placeholder="Event"
                                                    style={{ width: 140 }}
                                                    value={eventType}
                                                    onChange={(value) => {
                                                        setEventType(value);
                                                        setAppliedEventFilters((current) => ({ ...current, eventType: value }));
                                                    }}
                                                    options={[
                                                        { value: 'processed', label: 'processed' },
                                                        { value: 'delivered', label: 'delivered' },
                                                        { value: 'open', label: 'open' },
                                                        { value: 'click', label: 'click' },
                                                        { value: 'bounce', label: 'bounce' },
                                                        { value: 'reject', label: 'reject' },
                                                        { value: 'spam', label: 'spam' },
                                                        { value: 'unsubscribe', label: 'unsubscribe' },
                                                    ]}
                                                />
                                                <Input
                                                    placeholder="Recipient contains"
                                                    value={recipientFilter}
                                                    onChange={(event) => setRecipientFilter(event.target.value)}
                                                    onPressEnter={() => setAppliedEventFilters({ eventType, recipient: recipientFilter.trim() })}
                                                    style={{ width: 220 }}
                                                    allowClear
                                                />
                                                <Button onClick={() => setAppliedEventFilters({ eventType, recipient: recipientFilter.trim() })}>Apply</Button>
                                            </Space>
                                        )}
                                    >
                                        <Typography.Text type="secondary">
                                            Includes provider delivery IDs and SMTP diagnostics so you can inspect follow-up outcomes and failures per recipient.
                                        </Typography.Text>
                                        {loading ? <Spin /> : (
                                            <Table
                                                style={{ marginTop: 12 }}
                                                rowKey={(record) => record._id}
                                                dataSource={events}
                                                columns={eventColumns}
                                                pagination={{ pageSize: 12 }}
                                                scroll={{ x: 2400 }}
                                            />
                                        )}
                                    </Card>
                                </>
                            ),
                        },
                    ]}
                />

                <Card title="Solar Calc Outbound Email Summary" style={{ marginBottom: 16 }}>
                    <Row gutter={[12, 12]}>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Sent</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.sent || 0}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Delivered</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.delivered || 0}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Opened</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.opened || 0}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Clicked</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.clicked || 0}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Bounced</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.bounced || 0}</Typography.Title></Col>
                        <Col xs={12} md={4}><Typography.Text type="secondary">Campaigns</Typography.Text><Typography.Title level={4} style={{ margin: 0 }}>{solmatixSummary.uniqueCampaigns || 0}</Typography.Title></Col>
                    </Row>
                    <div style={{ marginTop: 16 }}>
                        <Typography.Text strong>Top click destinations</Typography.Text>
                        <div style={{ marginTop: 8 }}>
                            {(solmatixSummary.topLandingPages || []).length > 0 ? (
                                <Space direction="vertical" size={4}>
                                    {solmatixSummary.topLandingPages.map((entry) => (
                                        <div key={entry.url}>
                                            <Typography.Text>{entry.url}</Typography.Text>
                                            <Tag color="blue" style={{ marginLeft: 8 }}>{entry.count}</Tag>
                                        </div>
                                    ))}
                                </Space>
                            ) : (
                                <Typography.Text type="secondary">No click destinations yet.</Typography.Text>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </PageLayout>
    );
};

export default OutreachMonitor;
